import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that renders one box per SIZE variant, each with its own
 * stock status and quantity stepper, and adds every variant with a quantity
 * greater than zero to the cart in a single action.
 *
 * Color is chosen in a separate picker. When the color changes, each size box
 * is re-targeted client-side (stock, ETA, price, variant id) WITHOUT resetting
 * the quantities the customer has already typed.
 */
class VariantOrderGrid extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  /** @type {Array<Record<string, any>>} */
  #variants = [];

  /** @type {AbortController | undefined} */
  #variantFetchController;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('keydown', this.#onKeydown);

    this.#variants = this.#readVariants();

    // Follow the color chosen in the separate picker on the same section.
    if (Number(this.dataset.colorPosition) > 0) {
      this.#section = this.closest('.shopify-section, dialog');
      this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }

    // Initial state for the app's notify-me form (selected box is set server-side).
    this.#updateNotifyForm();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#notifyObserver?.disconnect();
    this.#notifyObserver = undefined;
  }

  #readVariants() {
    const script = this.querySelector('script[data-vog-variants]');
    if (!script?.textContent) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('variant-order-grid: invalid variant data', error);
      return [];
    }
  }

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    // Ignore the event we dispatched ourselves (avoids re-applying our own change).
    if (event.target === this) return;
    if (event.detail.data.newProduct) return;
    if (event.detail.data.productId !== this.dataset.productId) return;

    const colorIndex = Number(this.dataset.colorPosition) - 1;
    const color = event.detail.resource?.options?.[colorIndex];
    if (color == null) return;

    this.#applyColor(String(color));
  };

  /**
   * Re-target every size box to the variant matching (its size + the new color).
   * Quantities are preserved (clamped to the new variant's max).
   * @param {string} color
   */
  #applyColor(color) {
    for (const item of this.querySelectorAll('[data-vog-item]')) {
      const size = /** @type {HTMLElement} */ (item).dataset.size;
      const variant = this.#variants.find((entry) => entry.color === color && entry.size === size);
      this.#applyVariantToItem(/** @type {HTMLElement} */ (item), variant);
    }

    // The selected size now targets a different variant — re-evaluate the
    // notify-me form for the new color's stock.
    this.#updateNotifyForm();
  }

  /**
   * @param {HTMLElement} item
   * @param {Record<string, any> | undefined} variant
   */
  #applyVariantToItem(item, variant) {
    const input = /** @type {HTMLInputElement | null} */ (item.querySelector('input[data-variant-id]'));
    const minus = item.querySelector('[data-vog-step="minus"]');
    const plus = item.querySelector('[data-vog-step="plus"]');
    const etaEl = item.querySelector('[data-vog-eta]');

    // Size not offered in this color: hide and skip it from the cart.
    if (!variant) {
      item.setAttribute('hidden', '');
      if (input) input.disabled = true;
      return;
    }
    item.removeAttribute('hidden');

    const { status } = this.#statusFor(variant);

    item.classList.remove(
      'variant-order-grid__item--in_stock',
      'variant-order-grid__item--low',
      'variant-order-grid__item--out_of_stock'
    );
    item.classList.add(`variant-order-grid__item--${status}`);
    item.classList.toggle('variant-order-grid__item--unavailable', !variant.available);

    // ETA only shows for variants with quantity 0.
    if (etaEl) {
      if (variant.eta && Number(variant.qty) <= 0) {
        etaEl.textContent = `${this.dataset.etaLabel || ''} ${variant.eta}`.trim();
        etaEl.removeAttribute('hidden');
      } else {
        etaEl.textContent = '';
        etaEl.setAttribute('hidden', '');
      }
    }


    if (input) {
      input.dataset.variantId = String(variant.id);
      input.step = String(variant.step || 1);
      if (variant.max != null) input.max = String(variant.max);
      else input.removeAttribute('max');
      input.disabled = !variant.available;
      // Keep the typed quantity, but clamp to the new max.
      this.#setQuantity(input, this.#getQuantity(input));
    }

    const disabled = !variant.available;
    if (minus instanceof HTMLButtonElement) minus.disabled = disabled;
    if (plus instanceof HTMLButtonElement) plus.disabled = disabled;
  }

  /**
   * The currently selected size box (the chosen variant), if any.
   * @returns {HTMLElement | null}
   */
  #getSelectedItem() {
    return this.querySelector('.variant-order-grid__item--current:not([hidden])');
  }

  /**
   * Marks a size box as the active selection (highlights it) and makes it the
   * chosen variant so the image / price / URL update. Selection is independent
   * of quantity — each box keeps its own quantity for the multi-add.
   * @param {HTMLElement | null} item
   */
  #selectItem(item) {
    if (!item || item.hasAttribute('hidden')) return;

    for (const box of this.querySelectorAll('[data-vog-item]')) {
      const selected = box === item;
      box.classList.toggle('variant-order-grid__item--current', selected);
      box.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    const input = /** @type {HTMLInputElement | null} */ (item.querySelector('input[data-variant-id]'));
    if (input) {
      const variant = this.#variants.find((entry) => String(entry.id) === input.dataset.variantId);
      if (variant) this.#selectVariant(variant);
    }

    this.#updateNotifyForm();
  }

  /** @type {boolean} */
  #notifyFormVisible = false;

  /** @type {MutationObserver | undefined} */
  #notifyObserver;

  /**
   * Shows the back-in-stock app's "Notify me when available" form
   * (#esc-oos-form) only while the SELECTED variant is out of stock
   * (which also covers a fully sold-out product — every selection is
   * out of stock then). Hides it for in-stock / low-stock selections.
   */
  #updateNotifyForm() {
    const selectedInput = /** @type {HTMLInputElement | null} */ (
      this.#getSelectedItem()?.querySelector('input[data-variant-id]') ?? null
    );
    const variant = this.#variants.find((entry) => String(entry.id) === selectedInput?.dataset.variantId);

    this.#notifyFormVisible = variant
      ? (variant.managed && Number(variant.qty) <= 0) || variant.available === false
      : false;
    this.#applyNotifyFormState();
  }

  #applyNotifyFormState() {
    // Show the notify form while the selected variant is out of stock; the
    // Add to Basket button stays visible either way.
    const form = /** @type {HTMLElement | null} */ (document.querySelector('#esc-oos-form'));

    if (!form) {
      // The app injects the form after page load; apply the state as soon
      // as it appears, then stop watching.
      if (!this.#notifyObserver && document.body) {
        this.#notifyObserver = new MutationObserver(() => {
          if (document.querySelector('#esc-oos-form')) {
            this.#notifyObserver?.disconnect();
            this.#notifyObserver = undefined;
            this.#applyNotifyFormState();
          }
        });
        this.#notifyObserver.observe(document.body, { childList: true, subtree: true });
      }
      return;
    }

    form.style.display = this.#notifyFormVisible ? '' : 'none';
  }

  /**
   * Makes a variant the active selection: updates the URL and fetches the
   * product page for it, then dispatches variant:update so the theme refreshes
   * the gallery, price, etc.
   * @param {Record<string, any>} variant
   */
  async #selectVariant(variant) {
    const productUrl = this.dataset.productUrl || window.location.pathname;

    // Update the address bar (?variant=...), like the standard picker.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      history.replaceState({}, '', url.toString());
    } catch (_) {
      // Ignore URL failures (e.g. sandboxed iframes).
    }

    this.#variantFetchController?.abort();
    this.#variantFetchController = new AbortController();

    try {
      // Fetch like the theme's variant picker (?option_values=...), falling back
      // to ?variant= only if option-value IDs aren't available.
      const base = productUrl.split('?')[0];
      const requestUrl = variant.optionValues
        ? `${base}?option_values=${variant.optionValues}`
        : `${base}?variant=${variant.id}`;
      const response = await fetch(requestUrl, { signal: this.#variantFetchController.signal });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      // Dispatch on the section element so section-scoped listeners
      // (media-gallery, product-price, etc.) reliably receive it.
      const dispatcher = this.#section ?? this;
      dispatcher.dispatchEvent(
        new VariantUpdateEvent({ id: variant.id, available: variant.available }, `vog-${variant.id}`, {
          html,
          productId: this.dataset.productId ?? '',
        })
      );
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') console.error(error);
    }
  }

  /**
   * Replicates the Liquid threshold logic for a variant data object.
   * @param {Record<string, any>} variant
   */
  #statusFor(variant) {
    // Each variant carries its own size-based threshold (5 for large items,
    // 10 for smaller); fall back to the block default if missing.
    const threshold = Number(variant.threshold) || Number(this.dataset.threshold) || 10;
    let status = 'in_stock';

    if (variant.managed) {
      if (variant.qty <= 0) status = 'out_of_stock';
      else if (variant.qty < 5) status = 'low'; // Less Stock: 1-4
      else if (variant.qty > threshold) status = 'in_stock';
      else status = 'in_stock'; // 5..threshold -> In Stock
    }

    const textMap = {
      in_stock: this.dataset.textInStock || '',
      low: this.dataset.textLow || '',
      out_of_stock: this.dataset.textOutOfStock || '',
    };

    return { status, statusText: textMap[status] };
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const addButton = target.closest('[data-vog-add]');
    if (addButton) {
      this.#addToCart();
      return;
    }

    const item = /** @type {HTMLElement | null} */ (target.closest('[data-vog-item]'));

    // Stepper: adjust this box's quantity and select the box.
    const step = target.closest('[data-vog-step]');
    if (step && item) {
      const input = /** @type {HTMLInputElement | null} */ (item.querySelector('input[data-variant-id]'));
      if (input instanceof HTMLInputElement && !input.disabled) {
        const direction = step.getAttribute('data-vog-step') === 'plus' ? 1 : -1;
        const stepSize = parseInt(input.step, 10) || 1;
        this.#setQuantity(input, this.#getQuantity(input) + direction * stepSize);
      }
      this.#selectItem(item);
      return;
    }

    // Clicking anywhere else on a box selects that size.
    if (item) this.#selectItem(item);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-vog-step], input, [data-vog-add]')) return;
    const item = /** @type {HTMLElement | null} */ (target.closest('[data-vog-item]'));
    if (item) {
      event.preventDefault();
      this.#selectItem(item);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Built-in color selector: re-target the size boxes to the chosen color
    // and keep the same size selected (image / price / URL follow).
    const colorRadio = target.closest('input[data-vog-color]');
    if (colorRadio instanceof HTMLInputElement) {
      const previousSize = this.#getSelectedItem()?.dataset.size;
      this.#applyColor(colorRadio.value);
      const label = this.querySelector('[data-vog-color-label]');
      if (label) label.textContent = colorRadio.value;
      for (const color of this.querySelectorAll('.variant-order-grid__color')) {
        color.classList.toggle(
          'variant-order-grid__color--selected',
          color.contains(colorRadio) && colorRadio.checked
        );
      }

      // Re-select the same size in the new color, or the first available one.
      const items = [...this.querySelectorAll('[data-vog-item]:not([hidden])')];
      const next =
        items.find((box) => /** @type {HTMLElement} */ (box).dataset.size === previousSize) || items[0];
      this.#selectItem(/** @type {HTMLElement} */ (next));
      return;
    }

    const input = target.closest('input[data-variant-id]');
    if (input instanceof HTMLInputElement) {
      this.#setQuantity(input, this.#getQuantity(input));
      this.#selectItem(/** @type {HTMLElement | null} */ (input.closest('[data-vog-item]')));
    }
  };

  /** @param {HTMLInputElement} input */
  #getQuantity(input) {
    const value = parseInt(input.value, 10);
    return Number.isNaN(value) ? 0 : value;
  }

  /**
   * @param {HTMLInputElement} input
   * @param {number} value
   */
  #setQuantity(input, value) {
    const min = input.min ? parseInt(input.min, 10) : 1;
    let next = value;
    if (next < min) next = min;

    const max = input.max ? parseInt(input.max, 10) : null;
    if (max != null && next > max) next = max;

    input.value = String(next);
  }

  async #addToCart() {
    // Add every size whose quantity is greater than zero.
    const inputs = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[data-variant-id]')]);
    const items = inputs
      .filter((input) => !input.disabled && !input.closest('[data-vog-item]')?.hasAttribute('hidden'))
      .map((input) => ({ id: input.dataset.variantId, quantity: this.#getQuantity(input) }))
      .filter((item) => item.id && item.quantity > 0);

    if (items.length === 0) {
      this.#showError(this.dataset.emptyError);
      return;
    }

    this.#clearError();

    const addButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-vog-add]'));
    if (addButton) addButton.disabled = true;

    const formData = new FormData();
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, String(item.id));
      formData.append(`items[${index}][quantity]`, String(item.quantity));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (data.status) {
        this.#showError(data.description || data.message);
        this.dispatchEvent(
          new CartErrorEvent(this.id || 'variant-order-grid', data.message, data.description, data.errors)
        );
        return;
      }

      // Get the true cart total so the cart bubble shows the right number.
      let itemCount = items.reduce((total, item) => total + item.quantity, 0);
      try {
        const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
        if (typeof cart?.item_count === 'number') itemCount = cart.item_count;
      } catch (_) {
        // Fall back to the added count if the cart fetch fails.
      }

      // No `sections` in the payload -> cart-items-component re-renders itself.
      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'variant-order-grid', {
          source: 'variant-order-grid',
          itemCount,
          productId: this.dataset.productId,
        })
      );

      // Reset all quantities back to zero after a successful add.
      inputs.forEach((input) => this.#setQuantity(input, 0));
      this.#announce(this.dataset.addedText);
      this.#openCartDrawer();
    } catch (error) {
      console.error(error);
      this.#showError(this.dataset.errorText);
    } finally {
      if (addButton) addButton.disabled = false;
    }
  }

  /**
   * Opens the cart drawer if one exists on the page. Safe to call even when the
   * theme's auto-open setting is on (opening an already-open dialog is a no-op).
   */
  #openCartDrawer() {
    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (typeof drawer?.open === 'function') drawer.open();
  }

  /** @param {string | undefined} message */
  #showError(message) {
    const element = this.querySelector('[data-vog-error]');
    if (element) {
      element.textContent = message || '';
      element.classList.remove('hidden');
    }
  }

  #clearError() {
    const element = this.querySelector('[data-vog-error]');
    if (element) {
      element.textContent = '';
      element.classList.add('hidden');
    }
  }

  /** @param {string | undefined} message */
  #announce(message) {
    const element = this.querySelector('[data-vog-live]');
    if (element) element.textContent = message || '';
  }
}

if (!customElements.get('variant-order-grid')) {
  customElements.define('variant-order-grid', VariantOrderGrid);
}
